import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Mail, MessageCircle, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIMEZONES = ["Europe/Istanbul", "Europe/London", "Europe/Berlin", "America/New_York", "Asia/Dubai", "UTC"];

const Toggle = ({ id, label, hint, checked, onChange }) => (
  <label htmlFor={id} className="flex cursor-pointer items-start justify-between gap-4 py-4">
    <span>
      <span className="block text-sm font-semibold text-white">{label}</span>
      <span className="mt-1 block text-xs leading-relaxed text-mist">{hint}</span>
    </span>
    <span className="relative mt-0.5 inline-flex shrink-0">
      <input
        id={id}
        data-testid={id}
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="h-6 w-11 rounded-full bg-white/15 transition-colors peer-checked:bg-gold" />
      <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white transition-transform peer-checked:translate-x-5" />
    </span>
  </label>
);

async function post(path) {
  const r = await fetch(`${API}${path}`, { method: "POST", credentials: "include" });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Request failed");
  return data;
}

export const NotificationSettings = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    fetch(`${API}/admin/settings`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setS)
      .catch(() => toast.error("Could not load notification settings."));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy("save");
    try {
      const r = await fetch(`${API}/admin/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          alert_email: s.alert_email || null,
          instant_alerts: s.instant_alerts,
          weekly_digest: s.weekly_digest,
          timezone: s.timezone,
          digest_hour: s.digest_hour,
          whatsapp_number: s.whatsapp_number || null,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Invalid settings");
      setS(data);
      toast.success("Notification settings saved.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy("");
    }
  };

  const run = async (key, path, ok) => {
    setBusy(key);
    try {
      const data = await post(path);
      toast.success(ok(data));
      if (data.count !== undefined) setS((p) => ({ ...p, last_digest_at: new Date().toISOString() }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy("");
    }
  };

  if (!s) return null;
  const inboxLive = Boolean(s.alert_email);

  return (
    <form
      onSubmit={save}
      data-testid="notification-settings"
      className="mt-10 border border-white/10 bg-panel p-6 sm:p-8"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-gold">
          <Bell className="h-4 w-4" /> Notifications
        </p>
        <span
          data-testid="alert-inbox-status"
          className={`rounded-full px-3 py-1 font-mono text-[11px] uppercase tracking-[0.15em] ${
            inboxLive ? "bg-gold/15 text-gold" : "bg-white/10 text-white/60"
          }`}
        >
          {inboxLive ? `Live · ${s.source}` : "Inbox not set"}
        </span>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div>
          <label htmlFor="alert-email" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-white/60">
            Team alert inbox
          </label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
            <input
              id="alert-email"
              data-testid="alert-email-input"
              type="email"
              value={s.alert_email || ""}
              onChange={(e) => setS({ ...s, alert_email: e.target.value })}
              placeholder="team@yuxel.com"
              className="w-full rounded-xl border border-white/15 bg-surface py-3.5 pl-11 pr-4 text-base text-white outline-none transition-colors focus:border-gold"
            />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="digest-tz" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-white/60">
                Timezone
              </label>
              <select
                id="digest-tz"
                data-testid="digest-timezone-select"
                value={s.timezone}
                onChange={(e) => setS({ ...s, timezone: e.target.value })}
                className="w-full rounded-xl border border-white/15 bg-surface px-3 py-3 text-sm text-white outline-none focus:border-gold"
              >
                {TIMEZONES.map((t) => (
                  <option key={t} value={t} className="bg-panel">{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="digest-hour" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-white/60">
                Monday at
              </label>
              <select
                id="digest-hour"
                data-testid="digest-hour-select"
                value={s.digest_hour}
                onChange={(e) => setS({ ...s, digest_hour: Number(e.target.value) })}
                className="w-full rounded-xl border border-white/15 bg-surface px-3 py-3 text-sm text-white outline-none focus:border-gold"
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h} className="bg-panel">{String(h).padStart(2, "0")}:00</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="divide-y divide-white/10 border-t border-white/10 lg:border-t-0 lg:pt-0">
          <Toggle
            id="instant-alerts-toggle"
            label="Instant alerts"
            hint="Email the inbox the moment a consultation request lands."
            checked={s.instant_alerts}
            onChange={(v) => setS({ ...s, instant_alerts: v })}
          />
          <Toggle
            id="weekly-digest-toggle"
            label="Monday digest"
            hint="One summary email every Monday morning with the week's requests and status counts."
            checked={s.weekly_digest}
            onChange={(v) => setS({ ...s, weekly_digest: v })}
          />
          <div className="py-4">
            <label htmlFor="whatsapp-number" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-white/60">
              WhatsApp button number
            </label>
            <div className="relative">
              <MessageCircle className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
              <input
                id="whatsapp-number"
                data-testid="whatsapp-number-input"
                type="tel"
                value={s.whatsapp_number || ""}
                onChange={(e) => setS({ ...s, whatsapp_number: e.target.value })}
                placeholder="994501234567"
                className="w-full rounded-xl border border-white/15 bg-surface py-3 pl-11 pr-4 text-sm text-white outline-none transition-colors focus:border-gold"
              />
            </div>
            <p className="mt-2 text-xs text-mist">Country code, digits only. Leave empty to hide the floating WhatsApp button on the site.</p>
          </div>
          {s.last_digest_at && (
            <p className="pt-3 font-mono text-[11px] uppercase tracking-[0.15em] text-white/40" data-testid="last-digest-at">
              Last digest · {new Date(s.last_digest_at).toLocaleString("en-GB")}
            </p>
          )}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3 border-t border-white/10 pt-6">
        <button
          type="submit"
          data-testid="save-settings-button"
          disabled={busy === "save"}
          className="rounded-full bg-gold px-6 py-2.5 text-sm font-semibold text-ink transition-colors hover:bg-champagne disabled:opacity-50"
        >
          {busy === "save" ? "Saving…" : "Save settings"}
        </button>
        <button
          type="button"
          data-testid="send-test-email-button"
          disabled={!inboxLive || Boolean(busy)}
          onClick={() => run("test", "/admin/settings/test-email", (d) => `Test alert sent to ${d.to}.`)}
          className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-gold hover:text-gold disabled:opacity-40"
        >
          <Send className="h-4 w-4" /> {busy === "test" ? "Sending…" : "Send test alert"}
        </button>
        <button
          type="button"
          data-testid="send-digest-now-button"
          disabled={!inboxLive || Boolean(busy)}
          onClick={() => run("digest", "/admin/digest/send", (d) => `Digest sent · ${d.count} lead${d.count === 1 ? "" : "s"} this week.`)}
          className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-gold hover:text-gold disabled:opacity-40"
        >
          {busy === "digest" ? "Sending…" : "Send digest now"}
        </button>
      </div>
    </form>
  );
};
